"use client";

import { Button } from "@/components/atoms/Button";
import { cn } from "@/lib/utils";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import * as React from "react";

const Collapsible = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.Trigger;

type CollapsibleContentProps = CollapsiblePrimitive.CollapsibleContentProps & {
  shouldAnimate?: boolean;
};

const CollapsibleContent = React.forwardRef<
  HTMLDivElement,
  CollapsibleContentProps
>(
  (
    {
      children,
      className,
      shouldAnimate = false,
      ...props
    }: CollapsibleContentProps,
    ref,
  ) => {
    return (
      <CollapsiblePrimitive.CollapsibleContent
        {...props}
        ref={ref}
        className={cn(className, {
          "tw-transition-all data-[state=closed]:tw-animate-collapsible-up data-[state=open]:tw-animate-collapsible-down tw-overflow-hidden":
            shouldAnimate,
        })}
      >
        {children}
      </CollapsiblePrimitive.CollapsibleContent>
    );
  },
);
CollapsibleContent.displayName = CollapsiblePrimitive.Content.displayName;

interface ExpandableContentProps {
  children: React.ReactNode;
  openTriggerLabel?: React.ReactNode | string;
  closeTriggerLabel?: React.ReactNode | string;
  classNames?: string;
  expandDown?: boolean;
  triggerShownBelowContent?: boolean;
  buttonAppearance?: "white" | "black" | "purple" | "gray" | "destructive";
  buttonVariant?:
    | "outline-1"
    | "outline-2"
    | "default"
    | "ghost"
    | "text-only"
    | "underline"
    | "underline-m";
  onHandleToggle?: (isOpen: boolean) => void;
  hideButtonOnOpen?: boolean;
  shouldAnimate?: boolean;
}

const ExpandableContent: React.FC<ExpandableContentProps> = ({
  children,
  openTriggerLabel = "",
  closeTriggerLabel = "",
  classNames,
  expandDown = true,
  triggerShownBelowContent = false,
  buttonAppearance = "black",
  buttonVariant = "default",
  hideButtonOnOpen = false,
  onHandleToggle = () => {},
  shouldAnimate = false,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const hideButton = isOpen && hideButtonOnOpen;

  const handleToggle = () => {
    setIsOpen(!isOpen);
    onHandleToggle(!isOpen);
  };

  const getTriggerLabel = () => {
    if (isOpen && closeTriggerLabel) {
      return closeTriggerLabel;
    }
    return openTriggerLabel;
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleToggle}
      className="tw-flex tw-flex-col tw-items-start"
    >
      <CollapsibleTrigger asChild>
        {!hideButton && (
          <Button
            variant={buttonVariant}
            appearance={buttonAppearance}
            aria-expanded={isOpen}
            aria-controls="expandable-content"
            className={cn(
              "",
              {
                "tw-order-1":
                  expandDown && !(triggerShownBelowContent && isOpen),
                "tw-order-2":
                  !expandDown || (triggerShownBelowContent && isOpen),
              },
              classNames,
            )}
          >
            {getTriggerLabel()}
          </Button>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent
        id="expandable-content"
        className={cn("tw-overflow-hidden tw-w-full", {
          "tw-order-2": expandDown && !(triggerShownBelowContent && isOpen),
          "tw-order-1": !expandDown || (triggerShownBelowContent && isOpen),
        })}
        shouldAnimate={shouldAnimate}
      >
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
};

ExpandableContent.displayName = "ExpandableContent";

export {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  ExpandableContent,
};
